import type { Exercise } from "../lib/api";

interface Props {
  exercise: Pick<Exercise, "name_ru" | "equipment" | "image_url">;
  /** Крупный вариант для шапки экрана упражнения. */
  large?: boolean;
}

/**
 * Иконка снаряда — там, где выверенной иллюстрации нет.
 *
 * Четыре формы на десять видов снаряда: штанга, гантель, тренажёр и своё тело.
 * Дробить мельче нечем — на 34 пикселях блок и трос неразличимы, а иконка
 * должна отвечать на вопрос «чем это делают», а не изображать инвентарь.
 */
const GLYPH: Record<string, JSX.Element> = {
  bar: (
    <>
      <path d="M4 12h16" />
      <path d="M7 8v8M17 8v8" />
      <path d="M4.5 10v4M19.5 10v4" />
    </>
  ),
  dumbbell: (
    <>
      <path d="M9 12h6" />
      <path d="M6.5 8.5v7M17.5 8.5v7" />
      <path d="M4 10.5v3M20 10.5v3" />
    </>
  ),
  machine: (
    <>
      <rect x="4" y="4" width="7" height="16" rx="1.5" />
      <path d="M14 7h6M14 12h6M14 17h6" />
    </>
  ),
  body: (
    <>
      <circle cx="12" cy="6.5" r="2.5" />
      <path d="M12 9v6M8 11l4-1 4 1M9 20l3-5 3 5" />
    </>
  ),
};

const GLYPH_FOR: Record<string, keyof typeof GLYPH> = {
  barbell: "bar",
  ez_bar: "bar",
  smith: "bar",
  dumbbell: "dumbbell",
  kettlebell: "dumbbell",
  machine: "machine",
  cable: "machine",
  bodyweight: "body",
  band: "body",
  other: "body",
};

export default function ExerciseIcon({ exercise, large }: Props) {
  const size = large ? "exicon exicon--large" : "exicon";

  if (exercise.image_url) {
    // Маска, а не <img>: внутрь картинки, загруженной тегом img, CSS не
    // достаёт, и линии остались бы чёрными на чёрном фоне. Через маску
    // рисунок принимает цвет темы и одинаково читается в обеих.
    return (
      <span className={size} aria-hidden="true">
        <span
          className="exicon__art"
          style={{
            maskImage: `url(${exercise.image_url})`,
            WebkitMaskImage: `url(${exercise.image_url})`,
          }}
        />
      </span>
    );
  }

  return (
    <span className={size} aria-hidden="true">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {GLYPH[GLYPH_FOR[exercise.equipment] ?? "body"]}
      </svg>
    </span>
  );
}
